import React, { useState } from 'react'
import styled from 'styled-components'
import { Button, Flex, InfoIcon, Text, TextVariant } from '@mprolab/uikit'
import { useNavigate } from 'react-router-dom'
import useToast from 'hooks/useToast'
import {
  useCreateClanMutation,
  useGetClansQuery,
  useGetMyMembershipQuery,
  useGetMyPendingQuery,
  useRespondToClanRequestMutation,
  useCancelClanRequestMutation,
  useRequestToJoinClanMutation
} from 'state/services/community/community'
import { Card, Grow, Identity, Muted, Row, TableState, shortWallet } from './shared'
import { useSession } from './useSession'

const Input = styled.input`
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.white};
  font-size: 13px;
  outline: none;
  width: 100%;
  &:focus {
    border-color: ${({ theme }) => theme.colors.cyan};
  }
`

const Form = styled(Flex)`
  flex-direction: column;
  gap: 10px;
  width: 100%;
`

const Actions = styled(Flex)`
  gap: 8px;
  flex-wrap: wrap;
`

function errorText(error: unknown, fallback: string): string {
  const message = (error as { data?: { message?: string } })?.data?.message
  return typeof message === 'string' && message ? message : fallback
}

/**
 * Clans, and everything a person can do about them from one place.
 *
 * The order is deliberate: what is waiting for you, then where you already
 * are, then everywhere else. An invitation sitting unanswered is the thing
 * somebody came here for.
 */
const ClansPanel: React.FC = () => {
  const navigate = useNavigate()
  const { toastError, toastSuccess } = useToast()
  const { account, accessToken, ready } = useSession()
  const session = { account: account as string, accessToken: accessToken as string }

  const [search, setSearch] = useState('')
  const [name, setName] = useState('')
  const [tag, setTag] = useState('')

  /*
   * Freshness, not just invalidation.
   *
   * RTK Query tags only fire for actions taken in THIS browser, and every
   * interesting change here is made by somebody else: a leader approves, and
   * the applicant's tab knows nothing about it. That is how a member who had
   * already been accepted was still shown "Start a clan", "Ask to join" and a
   * member count of one — three statements, all false, none of them a server
   * bug. So these three refetch when the tab is opened and poll while it is.
   */
  const FRESH = { refetchOnMountOrArgChange: true, pollingInterval: 30_000 }
  const clans = useGetClansQuery({ search: search || undefined }, FRESH)
  const membership = useGetMyMembershipQuery(session, { skip: !ready, ...FRESH })
  const pending = useGetMyPendingQuery(session, { skip: !ready, ...FRESH })

  const [createClan, { isLoading: creating }] = useCreateClanMutation()
  const [respond] = useRespondToClanRequestMutation()
  /*
   * Which request is being answered, so only that row's buttons go quiet.
   * A single flag would disable every row; no flag at all lets a double press
   * answer the same request twice, and the second answer comes back as an
   * error about something the person has in fact just done successfully.
   */
  const [answering, setAnswering] = useState<string | null>(null)
  const [requestToJoin, { isLoading: requesting }] = useRequestToJoinClanMutation()
  const [cancelRequest] = useCancelClanRequestMutation()

  const myClan = membership.data?.clan ?? null
  const invites = pending.data?.invites ?? []
  const requests = pending.data?.requests ?? []
  const outgoing = pending.data?.outgoing ?? []
  /** Which clan (by tag) I have an unanswered request with, if any. */
  const requestedTag = outgoing[0]?.clanTag ?? null
  const requestedId = outgoing[0]?._id ?? null

  const onCreate = async () => {
    try {
      const created = await createClan({ ...session, name, tag }).unwrap()
      toastSuccess('Clan created', `${created.name} is yours to run.`)
      setName('')
      setTag('')
    } catch (error) {
      toastError('Could not create the clan', errorText(error, 'Try another name.'))
    }
  }

  const onRespond = async (requestId: string, accept: boolean) => {
    if (answering) return
    setAnswering(requestId)
    try {
      await respond({ ...session, requestId, accept }).unwrap()
      toastSuccess(accept ? 'Joined' : 'Declined')
    } catch (error) {
      toastError('Could not answer that', errorText(error, 'It may no longer be open.'))
    } finally {
      setAnswering(null)
    }
  }

  const onRequestJoin = async (clanTag: string) => {
    try {
      await requestToJoin({ ...session, tag: clanTag }).unwrap()
      toastSuccess('Request sent', 'The clan leader decides from here.')
    } catch (error) {
      toastError('Could not ask to join', errorText(error, 'Try again shortly.'))
    }
  }

  const onWithdraw = async () => {
    if (!requestedId) return
    try {
      await cancelRequest({ ...session, requestId: requestedId }).unwrap()
      toastSuccess('Request withdrawn')
    } catch (error) {
      toastError('Could not withdraw it', errorText(error, 'It may already be answered.'))
    }
  }

  return (
    <Flex flexDirection="column" style={{ gap: 16, width: '100%' }}>
      {ready && invites.length > 0 && (
        <Card>
          <Text variant={TextVariant.BODY_DEFAULT_MEDIUM}>Invitations waiting for you</Text>
          {invites.map((i) => (
            <Row key={i._id}>
              <Grow>
                <Text variant={TextVariant.BODY_DEFAULT_MEDIUM}>
                  {(i as unknown as { clanName?: string }).clanName ?? 'A clan'} invited you
                </Text>
                {i.message ? (
                  <Muted variant={TextVariant.BODY_SMALL}>{i.message}</Muted>
                ) : null}
              </Grow>
              <Actions>
                <Button
                  size="sm"
                  disabled={answering === i._id}
                  onClick={() => onRespond(i._id, true)}
                >
                  Accept
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={answering === i._id}
                  onClick={() => onRespond(i._id, false)}
                >
                  Decline
                </Button>
              </Actions>
            </Row>
          ))}
        </Card>
      )}

      {ready && requests.length > 0 && (
        <Card>
          <Text variant={TextVariant.BODY_DEFAULT_MEDIUM}>People asking to join your clan</Text>
          {requests.map((r) => (
            <Row key={r._id}>
              <Grow>
                <Text variant={TextVariant.BODY_DEFAULT_MEDIUM}>{shortWallet(r.wallet)}</Text>
                {r.message ? (
                  <Muted variant={TextVariant.BODY_SMALL}>{r.message}</Muted>
                ) : null}
              </Grow>
              <Actions>
                <Button
                  size="sm"
                  disabled={answering === r._id}
                  onClick={() => onRespond(r._id, true)}
                >
                  Approve
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={answering === r._id}
                  onClick={() => onRespond(r._id, false)}
                >
                  Reject
                </Button>
              </Actions>
            </Row>
          ))}
        </Card>
      )}

      {ready && !myClan && requestedTag && (
        <Card>
          <Row>
            <Grow>
              <Text variant={TextVariant.BODY_DEFAULT_MEDIUM}>
                Waiting on {outgoing[0]?.clanName ?? 'a clan'}
              </Text>
              <Muted variant={TextVariant.BODY_SMALL}>
                You asked to join @{requestedTag}. Its leader decides from here.
              </Muted>
            </Grow>
            <Actions>
              <Button size="sm" variant="secondary" onClick={onWithdraw}>
                Withdraw
              </Button>
            </Actions>
          </Row>
        </Card>
      )}

      {ready && myClan && (
        <Card>
          <Text variant={TextVariant.BODY_DEFAULT_MEDIUM}>Your clan</Text>
          <Row>
            <Grow>
              <Text variant={TextVariant.BODY_DEFAULT_MEDIUM}>{myClan.name}</Text>
              <Muted variant={TextVariant.BODY_SMALL}>
                @{myClan.tag} · {myClan.memberCount}{' '}
                {myClan.memberCount === 1 ? 'member' : 'members'} ·{' '}
                {membership.data?.role === 'leader' ? 'you lead it' : 'member'}
              </Muted>
            </Grow>
            <Button size="sm" onClick={() => navigate(`/community/clans/${myClan.tag}`)}>
              Open
            </Button>
          </Row>
        </Card>
      )}

      {!ready && (
        <Card>
          {/*
            The feature is shown to somebody who cannot yet use it, on purpose.
            Hiding it until sign-in is how a built feature reads as a missing
            one — which is exactly the report this came from.
          */}
          <Text variant={TextVariant.BODY_DEFAULT_MEDIUM}>
            Clans
            <InfoIcon
              iconProps={{ size: 14, style: { marginLeft: 6, verticalAlign: 'middle' } }}
              tooltipProps={{
                title:
                  "A wallet belongs to one clan at a time. A clan's power is the sum of its members' power, and its members vote together on DAO proposals."
              }}
            />
          </Text>
          <Muted variant={TextVariant.BODY_SMALL}>
            Sign in with your wallet to start or join one.
          </Muted>
        </Card>
      )}

      {ready && !myClan && !requestedTag && (
        <Card>
          <Text variant={TextVariant.BODY_DEFAULT_MEDIUM}>
            Start a clan
            <InfoIcon
              iconProps={{ size: 14, style: { marginLeft: 6, verticalAlign: 'middle' } }}
              tooltipProps={{
                title:
                  "A wallet belongs to one clan at a time, so you will have to leave this one before joining another. A clan needs at least two licence-holding members for its votes to count double."
              }}
            />
          </Text>
          {/*
            The rule still has to reach the person before the server refuses —
            a rule you cannot see is a rule you experience as a bug — but it
            fits behind an "i" rather than above the form.
          */}
          <Form>
            <Input
              placeholder="Clan name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <Input
              placeholder="Handle — a–z, 0–9 and underscore"
              value={tag}
              onChange={(e) => setTag(e.target.value.toLowerCase())}
            />
            <Button
              size="sm"
              disabled={creating || name.trim().length < 3 || tag.trim().length < 3}
              onClick={onCreate}
            >
              {creating ? 'Creating…' : 'Create clan'}
            </Button>
          </Form>
        </Card>
      )}

      <Card>
        <Flex style={{ gap: 12, alignItems: 'center', width: '100%' }}>
          <Text variant={TextVariant.BODY_DEFAULT_MEDIUM}>All clans</Text>
          <Grow>
            <Input
              placeholder="Search by name or handle"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Grow>
        </Flex>
        <TableState
          loading={clans.isLoading}
          available={!clans.isError}
          empty={(clans.data?.clans?.length ?? 0) === 0}
          emptyText="No clans yet. The first one is yours to start."
          unavailableText="Could not load clans just now."
        />
        {clans.data?.clans?.map((c) => (
          <Row key={c.id}>
            <Identity wallet={c.leaderWallet} name={c.name} tag={c.tag} avatarPath={c.avatarPath} />
            <Muted variant={TextVariant.BODY_SMALL}>
              {c.memberCount} {c.memberCount === 1 ? 'member' : 'members'}
            </Muted>
            <Actions>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => navigate(`/community/clans/${c.tag}`)}
              >
                View
              </Button>
              {/*
                One row, four possible truths: this is my clan, I have asked
                and am waiting, I may ask, or the clan is invitation only.
                Offering "Ask to join" for all of them is how a member ends up
                pressing it and being told 409.
              */}
              {ready && myClan?.tag === c.tag && (
                <Muted variant={TextVariant.BODY_SMALL}>Your clan</Muted>
              )}
              {ready && !myClan && requestedTag === c.tag && (
                <>
                  <Muted variant={TextVariant.BODY_SMALL}>Request pending</Muted>
                  <Button size="sm" variant="secondary" onClick={onWithdraw}>
                    Withdraw
                  </Button>
                </>
              )}
              {ready && !myClan && !requestedTag && c.openToRequests && (
                <Button
                  size="sm"
                  disabled={requesting}
                  onClick={() => onRequestJoin(c.tag)}
                >
                  {requesting ? 'Sending…' : 'Ask to join'}
                </Button>
              )}
            </Actions>
          </Row>
        ))}
      </Card>
    </Flex>
  )
}

export default ClansPanel
